<script lang="ts">
	import type { CloudImage } from "$lib/def/seo/cloud-image";
	import type { IDropdownOption } from "$lib/def/util-types";

  import { disableEdit, editing$, enableEdit, get } from "$lib/serv/seo";
  import { BehaviorSubject } from "rxjs";
  import { onMount } from "svelte";

  import Confirm from "./editors/Confirm.svelte";
  import ImageForm from "./editors/ImageForm.svelte";
  import LookupForm from "./editors/LookupForm.svelte";
  import OptionsForm from "./editors/OptionsForm.svelte";
  import StringForm from "./editors/StringForm.svelte";
  import StringsForm from "./editors/StringsForm.svelte";

	let modalTitle: string;
	let images: CloudImage[] = [];
	let subjImages = new BehaviorSubject<CloudImage[]>(images);
	export const images$ = subjImages.asObservable();
	let validator: (value: string | undefined) => string | undefined;
	let validationError: string | undefined;

	onMount(async () => {
		images = await get('image').then(col => col?.map(([,img]) => img as CloudImage)) ?? [];
		subjImages.next(images);
	})
	//###########################################################
	// Confirm
	//###########################################################

	export const confirm = (title?: string) => {
		if (currentConfirmationResolve) currentConfirmationResolve(); // this should never happen, but we cannot risk an unresolved promise
		modalTitle = title ?? 'Enter a value';
		return new Promise<boolean|undefined>((resolve) => currentConfirmationResolve = resolve)
	}

	let currentConfirmationResolve: ((value?:boolean) => void) | undefined;

	function confirmComplete(value?:boolean) {
		currentConfirmationResolve?.(value);
		currentConfirmationResolve = undefined;
	}

	//###########################################################
	// Image editing
	//###########################################################

	export const editImage = (current:string, legacyMethod = false) => {
		if (currentImageEditResolve) currentImageEditResolve({lib:images}); // this should never happen, but we cannot risk an unresolved promise
		editImageLegacyMethod = legacyMethod;
		return new Promise<{img?:CloudImage, lib:CloudImage[]}>((resolve) => currentImageEditResolve = resolve)
	}

	let editImageLegacyMethod: boolean;
	let currentImageEditResolve: ((params:{img?:CloudImage, lib:CloudImage[]}) => void) | undefined;

	function imageEditComplete(img?:CloudImage) {
		if (img) {
			images.push(img);
			subjImages.next(images);
		}
		currentImageEditResolve?.({img, lib:images});
		currentImageEditResolve = undefined;
	}

	//###########################################################
	// String editing
	//###########################################################

	export const editString = (current:string, title?: string, valueInvalidMessage: typeof validator = () => undefined) => {
		if (currentStringEditResolve) currentStringEditResolve(); // this should never happen, but we cannot risk an unresolved promise
		modalTitle = title ?? 'Enter a value';
		currentStringValue = current;
		validator = valueInvalidMessage;
		return new Promise<string|undefined>((resolve) => currentStringEditResolve = resolve)
	}

	let currentStringValue: string | undefined;
	let currentStringEditResolve: ((value?:string) => void) | undefined;

	function stringEditComplete(value?:string) {
		validationError = validator(value);
		if (validationError) return;
		currentStringEditResolve?.(value);
		currentStringEditResolve = undefined;
	}

	//###########################################################
	// Strings (array) editing
	//###########################################################

	export const editStrings = (current:(string|number)[], title?: string) => {
		if (currentStringsEditResolve) currentStringsEditResolve(); // this should never happen, but we cannot risk an unresolved promise
		modalTitle = title ?? 'Enter a value';
		currentStringsValue = current;
		return new Promise<(string|number)[]|undefined>((resolve) => currentStringsEditResolve = resolve)
	}

	let currentStringsValue: (string|number)[] | undefined;
	let currentStringsEditResolve: ((value?:(string|number)[]) => void) | undefined;

	function stringsEditComplete(value?:(string|number)[]) {
		currentStringsEditResolve?.(value);
		currentStringsEditResolve = undefined;
	}

	//###########################################################
	// Options editing
	//###########################################################

	export const editOption = <T extends (string | number)>(current:T, availableOptions:IDropdownOption<T>[]) => {
		if (currentOptionEditResolve) currentOptionEditResolve(); // this should never happen, but we cannot risk an unresolved promise
		options = availableOptions;
		currentOption = current;
		return new Promise<T|undefined>((resolve) => currentOptionEditResolve = resolve as typeof currentOptionEditResolve)
	}

	let options: IDropdownOption<string | number>[] | undefined;
	let currentOption: string | number | undefined;
	let currentOptionEditResolve: ((option?: string | number) => void) | undefined;

	function optionEditComplete(option?: string | number) {
		currentOptionEditResolve?.(option);
		currentOptionEditResolve = undefined;
	}

	//###########################################################
	// Lookup editing
	//###########################################################

	export const editLookup = <T extends (string | number)>(current:T, source:Promise<IDropdownOption<number>[]>) => {
		if (currentLookupEditResolve) currentLookupEditResolve(); // this should never happen, but we cannot risk an unresolved promise
		lookupSource = source;
		currentLookupValue = current;
		return new Promise<T|undefined>((resolve) => currentLookupEditResolve = resolve as typeof currentLookupEditResolve)
	}

	let lookupSource: Promise<IDropdownOption<number>[]> | undefined;
	let currentLookupValue: string | number | undefined;
	let currentLookupEditResolve: ((option?: string | number) => void) | undefined;

	function lookupEditComplete(option?: string | number) {
		currentLookupEditResolve?.(option);
		currentLookupEditResolve = undefined;
	}

</script>

{#if $editing$}
	{#if currentConfirmationResolve}
		<Confirm on:done={e => confirmComplete(e.detail)} prompt={modalTitle}/>
	{/if}
	{#if currentImageEditResolve}
		<ImageForm on:done={e => imageEditComplete(e.detail)} legacyMethod={editImageLegacyMethod}  />
	{/if}
	{#if currentStringEditResolve}
		<StringForm on:done={e => stringEditComplete(e.detail)} currentValue={currentStringValue} prompt={modalTitle} errorMessage={validationError} />
	{/if}
	{#if currentStringsEditResolve}
		<StringsForm on:done={e => stringsEditComplete(e.detail)} currentValues={currentStringsValue} prompt={modalTitle} />
	{/if}
	{#if currentOptionEditResolve && options}
		<OptionsForm on:done={e => optionEditComplete(e.detail)} {options} />
	{/if}
	{#if currentLookupEditResolve && lookupSource}
		<LookupForm on:done={e => lookupEditComplete(e.detail)} source={lookupSource} />
	{/if}

	<button class="fixed button" on:click={() => disableEdit()}>Done</button>
{:else}
	<button class="fixed button" on:click={() => enableEdit()}>Edit</button>
{/if}

<style lang="scss">
	.fixed.button {
		bottom: 32px;
		right: 32px;
		z-index: 2;
	}
	// .add {
	// 	position: absolute;
	// 	bottom: 0;
	// 	right: 0;
	// }
</style>